import React, { useEffect, useMemo, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export default function EmblaCarousel(props) {
  const {
    autoPlay = false,
    infiniteLoop = false,
    interval = 3000,
    transitionTime = 500,
    children,
    className = "",
    sx = {},
    initialIndex = 0,
    showIndicators = false,
    showSelectedSnap = true,
    showArrows = true,
    onSelect: onSelectCallback,
  } = props;

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const childCount = React.Children.count(children);
  // Only add autoplay plugin when we have 2+ slides (plugin's init bails when <= 1 and never sets delay, so play() would crash)
  const autoplayPlugin = useMemo(() => {
    if (autoPlay && childCount > 1) {
      return Autoplay({
        delay: interval,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
        playOnInit: false, // we start from our effect once we've verified snap list is ready
      });
    }
    return null;
  }, [autoPlay, interval, childCount]);

  const plugins = useMemo(() => {
    return autoplayPlugin ? [autoplayPlugin] : [];
  }, [autoplayPlugin]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: infiniteLoop,
      align: "center",
      slidesToScroll: 1,
      dragFree: false,
    },
    plugins
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const idx = emblaApi.selectedScrollSnap();
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
    setSelectedIndex(idx);
    onSelectCallback?.(idx);
  }, [emblaApi, onSelectCallback]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Scroll to initial index when carousel is ready
  useEffect(() => {
    if (emblaApi && initialIndex > 0) {
      emblaApi.scrollTo(initialIndex);
    }
  }, [emblaApi, initialIndex]);

  // Start/restart autoplay when plugin or emblaApi changes (only when 2+ snaps, matching plugin's init guard)
  useEffect(() => {
    if (!emblaApi || !autoplayPlugin) return;

    const snapList = emblaApi.scrollSnapList();
    if (!snapList || snapList.length <= 1) return;

    // Stop any existing autoplay
    if (typeof autoplayPlugin.stop === "function") {
      autoplayPlugin.stop();
    }

    // Start autoplay
    if (typeof autoplayPlugin.play === "function") {
      autoplayPlugin.play();
    }

    return () => {
      if (typeof autoplayPlugin.stop === "function") {
        autoplayPlugin.stop();
      }
    };
  }, [emblaApi, autoplayPlugin]);

  const childrenArray = React.Children.toArray(children);
  const hasMultipleSlides = childrenArray.length > 1;

  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ height: sx.height || "100%" }}>
        <div className={`embla ${className}`} ref={emblaRef} style={{ height: "100%" }}>
          <div className="embla__container" style={{ height: "100%" }}>
            {childrenArray.map((child, index) => (
              <div key={index} className="embla__slide">
                {child}
              </div>
            ))}
          </div>
        </div>
      </Box>
      {hasMultipleSlides && showArrows && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            mb: 0,
            px: 1,
            minHeight: "32px",
          }}
        >
          {/* Navigation buttons on the left */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              onClick={scrollPrev}
              disabled={!prevBtnEnabled && !infiniteLoop}
              aria-label="previous slide"
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={scrollNext}
              disabled={!nextBtnEnabled && !infiniteLoop}
              aria-label="next slide"
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Indicators or Selected Snap Display on the right */}
          {showIndicators && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {childrenArray.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => scrollTo(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: selectedIndex === index ? "primary.main" : "action.disabled",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: selectedIndex === index ? "primary.dark" : "action.hover",
                    },
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      scrollTo(index);
                    }
                  }}
                />
              ))}
            </Box>
          )}
          {showSelectedSnap && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="textSecondary">
                {selectedIndex + 1} / {childrenArray.length}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
